import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Action,
  Modal,
  SwitchInput,
  TextAreaInput,
  TextInput,
  SimpleSelect,
} from 'corso-admin-ui';
import {
  CrewMerchantUi,
  FinalizationEmailTypeEnum,
  helperSchema,
} from 'corso-types';
import { useId, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import api from '~/api';
import { useClaim } from '~/features/claimReview/ClaimReviewProvider';
import { useClaimSource } from '~/hooks/useClaimSource';
import { useConfigSettings } from '~/hooks/useConfigSettings';
import { useStoreId } from '~/providers/StoreUserProvider';

type EmailTemplate =
  CrewMerchantUi.SettingsConfig['email']['storeEmailTemplates']['finalizeEmailTemplates'][number];

type FinalizeClaimPayload = Parameters<
  ReturnType<ReturnType<(typeof api)['store']>['claim']>['finalize']
>[0];

const notifyCustomerByEmailSchema = z.object({
  emailType: z.nativeEnum(FinalizationEmailTypeEnum),
  subject: helperSchema.nonEmptyString,
  header: helperSchema.nonEmptyString,
  body: helperSchema.nonEmptyString,
});

const finalizeClaimPayloadSchema = z
  .object({
    noteToCustomer: helperSchema.nonEmptyString.optional(),
    /** @deprecated Need to unify claim source schemas. */ // TODO unify claim source schemas
    finalizationSource: z.union([
      z.literal('Merchant_App'),
      z.literal('Kustomer_App_Integration'),
      z.literal('Zendesk_App_Integration'),
    ]),
  })
  .and(
    z.discriminatedUnion('isNotifyCustomerByEmailEnabled', [
      z.object({
        isNotifyCustomerByEmailEnabled: z.literal(false),
        notifyCustomerByEmail: notifyCustomerByEmailSchema.optional(),
      }),
      z.object({
        isNotifyCustomerByEmailEnabled: z.literal(true),
        notifyCustomerByEmail: notifyCustomerByEmailSchema,
      }),
    ]),
  ) satisfies z.ZodSchema<FinalizeClaimPayload>;

export default function FinalizeClaim({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) {
  const formId = useId();
  const storeId = useStoreId();
  const claim = useClaim();

  const [addCustomerNote, setAddCustomerNote] = useState(false);
  const queryClient = useQueryClient();
  const finalizationSource = useClaimSource();

  // TODO review and see what happens when part of a claim failed to finalize, and we retry
  // TODO error state
  const { mutateAsync: finalizeClaim, isPending } = useMutation({
    mutationFn: api.store(storeId).claim(`${claim.id}`).finalize,
    onSettled: async () => {
      onClose();
      // * refetch the claim to update the status
      await claim.refetch();

      // * invalidate the claims query to update the  claims list
      await queryClient.invalidateQueries({ queryKey: ['claims'] });

      // * refetch the claim to update the status
      let attempts = 0;
      const intervalId = setInterval(() => {
        if (attempts >= 3) {
          clearInterval(intervalId);
          return;
        }

        claim
          .refetch()
          .catch((error) => console.error('Error refetching claim', error));

        attempts += 1;
      }, 2000);
    },
  });

  const {
    control,
    register,
    formState: { errors, isSubmitting },
    resetField,
    setValue,
    handleSubmit,
    reset,
  } = useForm<z.infer<typeof finalizeClaimPayloadSchema>>({
    resolver: zodResolver(finalizeClaimPayloadSchema),
    defaultValues: {
      isNotifyCustomerByEmailEnabled: false,
      finalizationSource,
    },
  });

  const isEmailEnabled = useWatch({
    control,
    name: 'isNotifyCustomerByEmailEnabled',
  });
  const { data: emailSettings } = useConfigSettings(({ email }) => email);
  const emailTemplateOptions = useMemo(
    () =>
      emailSettings?.storeEmailTemplates.finalizeEmailTemplates.filter(
        (template) =>
          template.type !== FinalizationEmailTypeEnum.claimNeedsMoreInfo,
      ) ?? [],
    [emailSettings?.storeEmailTemplates.finalizeEmailTemplates],
  );

  const onEmailTemplateChange = (template: EmailTemplate | null) => {
    if (!template) {
      resetField('notifyCustomerByEmail.subject');
      resetField('notifyCustomerByEmail.header');
      resetField('notifyCustomerByEmail.body');
      return;
    }

    setValue('notifyCustomerByEmail.subject', template.subject, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue('notifyCustomerByEmail.header', template.header, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue('notifyCustomerByEmail.body', template.body, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <Modal
      show={show}
      title="Finalize Claim"
      onClose={onClose}
      actions={
        <>
          <Action onClick={onClose}>Cancel</Action>
          <Action
            type="submit"
            variant="primary"
            loading={isSubmitting || isPending}
            form={formId}
          >
            Finalize
          </Action>
        </>
      }
    >
      <form
        id={formId}
        className="flex flex-col gap-6"
        onSubmit={(e) => {
          handleSubmit(
            (data) => finalizeClaim(data).then(() => reset()),
            (fieldError) => {
              console.error('Field Error', fieldError);
            },
          )(e).catch((error) => {
            console.error('Request Failed', error);
          });
        }}
      >
        <Controller
          control={control}
          name="isNotifyCustomerByEmailEnabled"
          render={({ field: { onChange, value } }) => (
            <SwitchInput
              id="notify-customer"
              label="Send Customer Email Notification"
              checked={value}
              onChange={(e) => {
                onChange(e);
                const { checked } = e.target;
                if (!checked) resetField('notifyCustomerByEmail');
              }}
            />
          )}
        />
        {isEmailEnabled && (
          <div className="flex flex-col gap-4">
            {emailTemplateOptions.length > 0 && (
              <Controller
                control={control}
                name="notifyCustomerByEmail.emailType"
                render={({ field: { value, onChange } }) => (
                  <SimpleSelect
                    label="Email Template"
                    options={emailTemplateOptions.map((template) => ({
                      label: template.name,
                      value: template.type,
                    }))}
                    value={value}
                    onChange={(templateType) => {
                      const template = emailTemplateOptions.find(
                        ({ type }) => type === templateType,
                      );
                      if (!template) {
                        console.error(
                          new Error(
                            `Template Not Found for Type: ${templateType}`,
                          ),
                        );
                        return; // maybe this should throw the error instead of logging and returning
                      }
                      onEmailTemplateChange(template);
                      onChange(templateType);
                    }}
                  />
                )}
              />
            )}
            <TextInput
              id="notify-customer-subject"
              label="Subject"
              required
              {...register('notifyCustomerByEmail.subject')}
              error={
                errors.notifyCustomerByEmail?.subject && 'Subject is required.'
              }
            />
            <TextInput
              id="notify-customer-header"
              label="Header"
              required
              {...register('notifyCustomerByEmail.header')}
              error={
                errors.notifyCustomerByEmail?.header && 'Header is required.'
              }
            />
            <TextAreaInput
              id="notify-customer-body"
              label="Body"
              required
              {...register('notifyCustomerByEmail.body')}
              error={errors.notifyCustomerByEmail?.body && 'Body is required.'}
            />
          </div>
        )}

        <SwitchInput
          id="add-customer-note"
          label="Add Customer Note"
          checked={addCustomerNote}
          onChange={(e) => {
            const { checked } = e.target;
            setAddCustomerNote(checked);
            if (!checked) resetField('noteToCustomer');
          }}
        />

        {addCustomerNote && (
          <TextAreaInput
            id="claim-level-note-to-customer"
            label="Customer Note"
            details="This note displays at the top of the customer's claim status page. This does not replace notes added for individual line items."
            required
            {...register('noteToCustomer')}
          />
        )}
      </form>
    </Modal>
  );
}
